export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function resolveApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  try {
    const win = window as Window & {
      __TAURI_INTERNALS__?: unknown;
      __TAURI__?: unknown;
    };
    const isTauri = Boolean(win.__TAURI_INTERNALS__ || win.__TAURI__);
    const envOrigin = (import.meta as ImportMeta & { env?: Record<string, string> }).env
      ?.VITE_APP_ORIGIN;
    if (isTauri && envOrigin) {
      return `${envOrigin.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
    }
  } catch {
    // fall through to relative
  }
  return path;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const startedAt = typeof performance === 'undefined' ? 0 : performance.now();
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init?.headers ?? {}),
  };
  const response = await fetch(resolveApiUrl(path), {
    ...init,
    credentials: 'include',
    headers,
  });

  if (typeof performance !== 'undefined' && typeof performance.measure === 'function') {
    const durationMs = performance.now() - startedAt;
    performance.measure(`spox:api:${init?.method ?? 'GET'} ${path}`, {
      start: startedAt,
      duration: durationMs,
      detail: {
        status: response.status,
        serverTiming: response.headers.get('server-timing'),
      },
    });
  }

  const payload = (await response.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string; details?: unknown };
  };

  if (!response.ok) {
    throw new ApiClientError(
      response.status,
      payload.error?.code ?? 'HTTP_ERROR',
      payload.error?.message ?? `Request failed: ${response.status}`,
      payload.error?.details,
    );
  }

  return payload as T;
}

export const api = {
  health: () => request<{ ok: boolean; service: string }>('/api/health'),
  ready: () => request<{ ok: boolean; ready: boolean }>('/api/health/ready'),
  getV03Operations:()=>request<{state:{state:string};aiControl:{state:string};lifecycleControl:{state:string};canary:null|{id:string;publication_id:string;state:string;expires_at:string};lifecycle:Record<string,number>;publishing:Record<string,number>;ai:Record<string,number>}>('/api/operations/v03'),
  listLifecycleCandidates:(publicationId:string)=>request<{items:Array<{analysisBundleId:string;sourceVersionId:string;sourceId:string;title:string;createdAt:string}>}>(`/api/operations/v03/lifecycle/candidates?publicationId=${encodeURIComponent(publicationId)}`),
  prepareAutonomyCanary:(input:{publicationId:string;autonomyPolicyId:string;scope:Record<string,string>;permittedActions:string[];permittedPlatforms:string[];permittedDestinationIds:string[];maxLifecycleActions:number;maxAiCalls:number;maxProviderActions:number;maxCostMicros:number;startsAt:string;expiresAt:string;reason:string})=>request<{item:{id:string;state:string;publicationId:string;autonomyPolicyId:string;scope:Record<string,string>;permittedActions:string[];maxLifecycleActions:number;maxAiCalls:number;maxProviderActions:number;maxCostMicros:number;startsAt:string;expiresAt:string}}>(`/api/operations/v03/canaries`,{method:'POST',body:JSON.stringify(input)}),
  transitionAutonomyCanary:(id:string,action:'activate'|'pause'|'terminate',reason:string)=>request<{item:{id:string;state:string}}>(`/api/operations/v03/canaries/${encodeURIComponent(id)}/${action}`,{method:'POST',body:JSON.stringify({reason})}),
  setLifecycleControl:(state:'LIFECYCLE_ENABLED'|'LIFECYCLE_PAUSED',reason:string)=>request<{item:{state:string}}>(`/api/operations/v03/lifecycle-control`,{method:'PUT',body:JSON.stringify({state,reason})}),
  listPublications: () => request<{ items: import('@domain/publication/types').Publication[] }>('/api/publications'),
  createPublication: (body: unknown) =>
    request<{ item: import('@domain/publication/types').Publication }>('/api/publications', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getPublication: (id: string) =>
    request<{ item: import('@domain/publication/types').Publication }>(`/api/publications/${id}`),
  getBoundedPublicationReadiness:(jobId:string)=>request<{item:import('@domain/v03').BoundedPublicationReadinessView}>(`/api/v03/publication-jobs/${jobId}/bounded-readiness`),
  executeBoundedPublication:(jobId:string)=>request<{permitId:string;record:Record<string,unknown>|null;item:Record<string,unknown>;job:Record<string,unknown>}>(`/api/v03/publication-jobs/${jobId}/bounded-execute`,{method:'POST',body:JSON.stringify({confirmation:'Publish this exact post once'})}),
  revalidateSocialOutputVersion:(publicationId:string,versionId:string)=>request<{item:import('@domain/v03').ReexecutionPreparationView}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/social-output-versions/${encodeURIComponent(versionId)}/revalidate`,{method:'POST'}),
  getEditorialProfile: (publicationId: string) =>
    request<{ item: import('@domain/editorial-profile/types').EditorialProfile | null }>(
      `/api/publications/${publicationId}/editorial-profile`,
    ),
  saveEditorialProfile: (publicationId: string, input: unknown, expectedVersion: number | null) =>
    request<{ item: import('@domain/editorial-profile/types').EditorialProfile }>(
      `/api/publications/${publicationId}/editorial-profile`,
      {
        method: 'POST',
        body: JSON.stringify({ input, expectedVersion }),
      },
    ),
  listToneRegisters: (publicationId: string) =>
    request<{ items: import('@domain/tone-register/types').ToneRegister[] }>(
      `/api/publications/${publicationId}/tone-registers`,
    ),
  createToneRegister: (publicationId: string, body: unknown) =>
    request<{ item: import('@domain/tone-register/types').ToneRegister }>(
      `/api/publications/${publicationId}/tone-registers`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  saveToneRegisterVersion: (registerId: string, input: unknown, expectedVersion: number) =>
    request<{ item: import('@domain/tone-register/types').ToneRegister }>(
      `/api/tone-registers/${registerId}/versions`,
      {
        method: 'POST',
        body: JSON.stringify({ input, expectedVersion }),
      },
    ),
  listSources: (publicationId: string, opts?: { includeDeleted?: boolean }) => {
    const qs = opts?.includeDeleted ? '?includeDeleted=1' : '';
    return request<{ items: import('@domain/source/types').Source[] }>(
      `/api/publications/${publicationId}/sources${qs}`,
    );
  },
  getSourceDesk: (publicationId: string) =>
    request<{
      items: Array<{
        source: import('@domain/source/types').Source;
        excerpt: string;
        publicationDate: string | null;
        publicationName: string | null;
        featuredMediaAssetId: string | null;
        featuredImageUrl: string | null;
        embeddedImageUrl: string | null;
        featuredMediaState: 'ready' | 'pending' | 'unavailable' | 'failed_retryable' | 'failed_terminal' | null;
        campaignCount: number;
        activeCampaignCount: number;
        potential: string | null;
        topics: string[];
        shelfLifeClass: string | null;
        freshnessHorizon: string | null;
        shelfLifeUrgency: string | null;
      }>;
    }>(`/api/publications/${publicationId}/sources/desk`),
  createSource: (publicationId: string, body: unknown) =>
    request<import('@domain/source/types').SourceWithVersions>(
      `/api/publications/${publicationId}/sources`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  getSource: (sourceId: string) =>
    request<import('@domain/source/types').SourceWithVersions>(`/api/sources/${sourceId}`),
  addSourceVersion: (sourceId: string, input: unknown, expectedCurrentVersionId?: string) =>
    request<import('@domain/source/types').SourceWithVersions>(`/api/sources/${sourceId}/versions`, {
      method: 'POST',
      body: JSON.stringify({ input, expectedCurrentVersionId }),
    }),
  requestAnalysis: (
    sourceId: string,
    body: {
      sourceVersionId: string;
      force?: boolean;
      acknowledgeInternalForExternal?: boolean;
    },
  ) =>
    request<{
      job: import('@domain/jobs/types').AsyncJob;
      analysisBundleId: string | null;
      reusedExisting: boolean;
    }>(`/api/sources/${sourceId}/analysis`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  runAnalysisJob: (jobId: string) =>
    request<{
      job: import('@domain/jobs/types').AsyncJob;
      detail: import('@domain/source-intelligence/types').AnalysisBundleDetail | null;
      error?: { code: string; message: string };
    }>(`/api/analysis/jobs/${jobId}/run`, { method: 'POST' }),
  getAnalysisJob: (jobId: string) =>
    request<{
      job: import('@domain/jobs/types').AsyncJob;
      analysisBundleId: string | null;
      detail: import('@domain/source-intelligence/types').AnalysisBundleDetail | null;
      failureCategory: string | null;
    }>(`/api/analysis/jobs/${jobId}`),
  retryAnalysisJob: (jobId: string) =>
    request<{
      job: import('@domain/jobs/types').AsyncJob;
      analysisBundleId: string | null;
      reusedExisting: boolean;
    }>(`/api/analysis/jobs/${jobId}/retry`, { method: 'POST' }),
  getAnalysisBundle: (bundleId: string) =>
    request<{
      item: import('@domain/source-intelligence/types').AnalysisBundleDetail;
      brief: import('@domain/source-intelligence/brief').ContentIntelligenceBriefView;
    }>(`/api/analysis/bundles/${bundleId}`),
  getVersionAnalysis: (sourceId: string, versionId: string) =>
    request<{
      item: import('@domain/source-intelligence/types').AnalysisBundleDetail | null;
      brief: import('@domain/source-intelligence/brief').ContentIntelligenceBriefView | null;
    }>(`/api/sources/${sourceId}/versions/${versionId}/analysis`),
  getContentIntelligenceBrief: (sourceId: string, versionId: string) =>
    request<import('@domain/source-intelligence/brief').ContentIntelligenceBriefEnvelope>(
      `/api/sources/${sourceId}/versions/${versionId}/brief`,
    ),
  listCampaigns: (publicationId: string, opts?: { includeAllLifecycle?: boolean }) => {
    const qs = new URLSearchParams({ publicationId });
    if (opts?.includeAllLifecycle) qs.set('includeAllLifecycle', '1');
    return request<{ items: import('@domain/campaign').CampaignView[] }>(
      `/api/campaigns?${qs.toString()}`,
    );
  },
  getCampaignDesk: (publicationId: string, from: string, to: string) => {
    const qs = new URLSearchParams({ publicationId, from, to });
    return request<{
      items: import('@domain/campaign').CampaignView[];
      extras: Array<{
        campaignId: string;
        approval: import('@domain/approval').CampaignApprovalSummary | null;
        schedule: import('@domain/scheduler').CampaignScheduleSummary | null;
        tracker: import('@domain/tracker').CampaignTrackerSummary | null;
        batches: import('@domain/generation').GenerationBatchView[];
      }>;
      timeline: import('@domain/scheduler').TimelineDeskView | null;
    }>(`/api/campaign-desk?${qs.toString()}`);
  },
  createCampaign: (body: import('@domain/campaign').CreateCampaignInput) =>
    request<{ item: import('@domain/campaign').CampaignView }>('/api/campaigns', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getCampaign: (campaignId: string) =>
    request<{ item: import('@domain/campaign').CampaignView }>(
      `/api/campaigns/${campaignId}`,
    ),
  getCampaignWorkspace: (campaignId: string) =>
    request<{ item: import('@domain/v03').CampaignWorkspaceView }>(
      `/api/campaigns/${campaignId}/workspace`,
    ),
  assessCampaignTemporalReadiness: (campaignId:string) =>
    request<{item:import('@domain/v03').TemporalAssessmentResult}>(`/api/v03/campaigns/${encodeURIComponent(campaignId)}/temporal-assessment`,{method:'POST'}),
  prepareManualSocialOutput: (publicationId:string, body:{campaignId:string;publishingDestinationId:string;text:string;idempotencyKey:string}) =>
    request<{item:import('@domain/v03').ManualSocialOutputPreparationView}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/manual-social-outputs`,{
      method:'POST',
      body:JSON.stringify(body),
    }),
  evaluateSocialOutputVersion:(id:string)=>request<{item:Record<string,unknown>}>(`/api/v03/social-output-versions/${encodeURIComponent(id)}/evaluate`,{method:'POST'}),
  assessSocialOutputVersionSafety:(id:string)=>request<{item:Record<string,unknown>}>(`/api/v03/social-output-versions/${encodeURIComponent(id)}/safety-assessment`,{method:'POST'}),
  verifyGhostSourcePublicPrivacy:(publicationId:string,sourceId:string,sourceVersionId:string)=>request<{item:Record<string,unknown>}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(sourceVersionId)}/verify-ghost-public`,{method:'POST'}),
  approveSocialOutputVersion:(id:string)=>request<{item:Record<string,unknown>}>(`/api/v03/social-output-versions/${encodeURIComponent(id)}/approval`,{method:'POST',body:JSON.stringify({decision:'approved'})}),
  scheduleExactSocialOutputVersion:(publicationId:string,id:string)=>request<{item:import('@domain/v03').OrchestrationPlanView}>('/api/orchestration/plan',{method:'POST',body:JSON.stringify({publicationId,socialOutputVersionId:id,triggerType:'manual',immediate:true})}),
  authoriseV03ScheduleItem:(id:string)=>request<{item:Record<string,unknown>}>(`/api/v03/schedule-items/${encodeURIComponent(id)}/authorise`,{method:'POST'}),
  enqueueV03ScheduleItem:(id:string)=>request<{item:Record<string,unknown>;job:Record<string,unknown>|null;reused:boolean}>(`/api/v03/schedule-items/${encodeURIComponent(id)}/enqueue`,{method:'POST'}),
  listMultiSourceCandidates: (publicationId:string) => request<{items:import('@domain/v03').MultiSourceCampaignCandidateView[]}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/multi-source-candidates`),
  acceptMultiSourceCandidate: (publicationId:string,candidateId:string) => request<{item:unknown}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/multi-source-candidates/${encodeURIComponent(candidateId)}/accept`,{method:'POST'}),
  dismissMultiSourceCandidate: (publicationId:string,candidateId:string,reason:string) => request<{item:import('@domain/v03').MultiSourceCampaignCandidateView}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/multi-source-candidates/${encodeURIComponent(candidateId)}/dismiss`,{method:'POST',body:JSON.stringify({reason})}),
  getLivingArchive: (publicationId:string,q='') => request<{items:import('@domain/v03').LivingArchiveRecordView[]}>(`/api/v03/living-archive?publicationId=${encodeURIComponent(publicationId)}&q=${encodeURIComponent(q)}`),
  getNewsContexts: (publicationId:string) => request<{items:import('@domain/v03').NewsContextView[]}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/news-contexts`),
  getResurfacingOpportunities: (publicationId:string) => request<{items:import('@domain/v03').ResurfacingOpportunityView[]}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/resurfacing/opportunities`),
  resolveResurfacingFactualReview: (publicationId:string,opportunityId:string,input:{outcome:import('@domain/v03').FactualReviewOutcome;reason:string}) => request<{item:unknown}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/resurfacing/opportunities/${encodeURIComponent(opportunityId)}/factual-review`,{method:'POST',body:JSON.stringify(input)}),
  decideResurfacingOpportunity: (publicationId:string,opportunityId:string,input:{decision:'accept'|'dismiss'|'defer';reason:string}) => request<{item:import('@domain/v03').ResurfacingOpportunityView}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/resurfacing/opportunities/${encodeURIComponent(opportunityId)}/decision`,{method:'POST',body:JSON.stringify(input)}),
  getAutonomyPolicy: (publicationId:string) => request<{item:import('@domain/v03').AdvancedAutonomyPolicy|null;history:import('@domain/v03').AdvancedAutonomyPolicy[]}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/autonomy-policy`),
  saveAutonomyPolicy: (publicationId:string,input:{reason:string;rules:Array<{capability:import('@domain/v03').AdvancedAutonomyCapability;level:import('@domain/v03').AdvancedAutonomyLevel;priority:number;conditions:Record<string,unknown>}>;confirmAutonomousPublishing?:string}) => request<{item:import('@domain/v03').AdvancedAutonomyPolicy}>(`/api/v03/publications/${encodeURIComponent(publicationId)}/autonomy-policy`,{method:'POST',body:JSON.stringify(input)}),
  getCurrentOrchestrationPlan: (publicationId: string) =>
    request<{ item: import('@domain/v03').OrchestrationPlanView | null }>(`/api/orchestration/current?publicationId=${encodeURIComponent(publicationId)}`),
  createOrchestrationPlan: (publicationId: string) =>
    request<{ item: import('@domain/v03').OrchestrationPlanView }>('/api/orchestration/plan', { method:'POST', body:JSON.stringify({ publicationId, triggerType:'manual' }) }),
  setV03ScheduleLock: (id: string, locked: boolean) =>
    request<{ ok: boolean }>(`/api/orchestration/schedule-items/${id}/lock`, { method:'POST', body:JSON.stringify({ locked }) }),
  getCampaignPlan: (campaignId: string) =>
    request<{ item: import('@domain/campaign').CampaignView }>(
      `/api/campaigns/${campaignId}/plan`,
    ),
  updateCampaignPlan: (
    campaignId: string,
    body: import('@domain/campaign').UpdateCampaignPlanInput,
  ) =>
    request<{ item: import('@domain/campaign').CampaignView }>(
      `/api/campaigns/${campaignId}/plan`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    ),
  requestGeneration: (
    campaignId: string,
    body: {
      expectedPlanRevision: number;
      mode?: 'initial' | 'regenerate';
      idempotencyKey?: string | null;
      allowStale?: boolean;
    },
  ) =>
    request<{ item: import('@domain/generation').GenerationBatchView }>(
      `/api/campaigns/${campaignId}/generation`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listGenerationBatches: (campaignId: string) =>
    request<{ items: import('@domain/generation').GenerationBatchView[] }>(
      `/api/campaigns/${campaignId}/generation-batches`,
    ),
  getGenerationBatch: (batchId: string) =>
    request<{ item: import('@domain/generation').GenerationBatchView }>(
      `/api/generation-batches/${batchId}`,
    ),
  runGenerationJob: (jobId: string) =>
    request<{ item: import('@domain/generation').GenerationBatchView }>(
      `/api/generation/jobs/${jobId}/run`,
      { method: 'POST' },
    ),
  editMasterPost: (
    postId: string,
    body: import('@domain/generation').EditMasterPostInput,
  ) =>
    request<{ item: import('@domain/generation').GenerationBatchView }>(
      `/api/master-posts/${postId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  listPlatforms: () =>
    request<{
      items: Array<{
        id: string;
        label: string;
        accountTypes: Array<{ id: string; label: string }>;
      }>;
    }>('/api/platforms'),
  listDestinations: (publicationId: string) =>
    request<{
      items: import('@domain/platform').PublishingDestination[];
      discoveryMode?: string;
    }>(`/api/publications/${publicationId}/destinations`),
  listDestinationViews: (publicationId: string) =>
    request<{
      items: import('@domain/platform').PublishingDestinationView[];
      discoveryMode?: string;
    }>(`/api/publications/${publicationId}/destinations?views=1`),
  createDestination: (
    publicationId: string,
    body: Omit<import('@domain/platform').CreatePublishingDestinationInput, 'publicationId'>,
  ) =>
    request<{ item: import('@domain/platform').PublishingDestination }>(
      `/api/publications/${publicationId}/destinations`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  fetchDestinationAccounts: (publicationId: string, mode?: 'auto' | 'fixture' | 'publer') =>
    request<{
      item: import('@domain/platform').DestinationDiscoveryResult & {
        discoveryMode: string;
      };
    }>(
      `/api/publications/${publicationId}/destinations/fetch${mode ? `?mode=${mode}` : ''}`,
      { method: 'POST' },
    ),
  resyncDestinationAccounts: (publicationId: string, mode?: 'auto' | 'fixture' | 'publer') =>
    request<{
      item: {
        discovery: import('@domain/platform').DestinationDiscoveryResult & {
          discoveryMode: string;
        };
        views: import('@domain/platform').PublishingDestinationView[];
      };
    }>(
      `/api/publications/${publicationId}/destinations/resync${mode ? `?mode=${mode}` : ''}`,
      { method: 'POST' },
    ),
  saveDiscoveredDestination: (
    publicationId: string,
    body:
      | { account: import('@domain/platform').SaveDiscoveredAccountInput['account'] }
      | { accounts: import('@domain/platform').SaveDiscoveredAccountInput['account'][] },
  ) =>
    request<{
      item:
        | import('@domain/platform').SaveDiscoveredAccountResult
        | import('@domain/platform').SaveSelectedAccountsResult;
    }>(`/api/publications/${publicationId}/destinations/save`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requestAdaptation: (
    campaignId: string,
    body: import('@domain/platform').RequestAdaptationInput,
  ) =>
    request<{ item: import('@domain/platform').AdaptationBatchView }>(
      `/api/campaigns/${campaignId}/adaptation`,
      { method: 'POST', body: JSON.stringify({ ...body, campaignId }) },
    ),
  runAdaptationJob: (jobId: string) =>
    request<{ item: import('@domain/platform').AdaptationBatchView }>(
      `/api/adaptation/jobs/${jobId}/run`,
      { method: 'POST' },
    ),
  listAdaptationBatches: (campaignId: string) =>
    request<{ items: import('@domain/platform').AdaptationBatchView[] }>(
      `/api/campaigns/${campaignId}/adaptation-batches`,
    ),
  getAdaptationBatch: (batchId: string) =>
    request<{ item: import('@domain/platform').AdaptationBatchView }>(
      `/api/adaptation-batches/${batchId}`,
    ),
  listPlatformVariants: (campaignId: string) =>
    request<{ items: import('@domain/platform').PlatformVariantBundle[] }>(
      `/api/campaigns/${campaignId}/platform-variants`,
    ),
  editPlatformVariant: (
    variantId: string,
    body: import('@domain/platform').EditPlatformVariantInput,
  ) =>
    request<{ item: import('@domain/platform').AdaptationBatchView }>(
      `/api/platform-variants/${variantId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  getVariantApproval: (variantId: string) =>
    request<{ item: import('@domain/approval').PlatformVariantApprovalState }>(
      `/api/platform-variants/${variantId}/approval`,
    ),
  listVariantApprovals: (variantId: string) =>
    request<{ items: import('@domain/approval').ApprovalRecordView[] }>(
      `/api/platform-variants/${variantId}/approvals`,
    ),
  submitVariantApproval: (
    variantId: string,
    body: import('@domain/approval').SubmitApprovalDecisionInput,
  ) =>
    request<{
      item: import('@domain/approval').ApprovalRecordView;
      state: import('@domain/approval').PlatformVariantApprovalState;
      created: boolean;
    }>(`/api/platform-variants/${variantId}/approvals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getCampaignApprovalSummary: (campaignId: string) =>
    request<{ item: import('@domain/approval').CampaignApprovalSummary }>(
      `/api/campaigns/${campaignId}/approval-summary`,
    ),
  listStudioQueue: (publicationId: string) =>
    request<{ items: import('@domain/studio').StudioQueueItem[] }>(
      `/api/studio/queue?publicationId=${encodeURIComponent(publicationId)}`,
    ),
  getVariantProduction: (variantId: string) =>
    request<{ item: import('@domain/studio').StudioVariantProductionView }>(
      `/api/platform-variants/${variantId}/production`,
    ),
  getProductionRequirement: (variantId: string) =>
    request<{ item: import('@domain/studio').ProductionRequirement }>(
      `/api/platform-variants/${variantId}/production-requirement`,
    ),
  overrideProductionRequirement: (
    variantId: string,
    body: import('@domain/studio').OverrideProductionRequirementInput,
  ) =>
    request<{ item: import('@domain/studio').ProductionRequirement }>(
      `/api/platform-variants/${variantId}/production-requirement/override`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  ensureProductionBrief: (variantId: string) =>
    request<{ item: import('@domain/studio').ProductionBrief }>(
      `/api/platform-variants/${variantId}/production-brief`,
      { method: 'POST', body: '{}' },
    ),
  listMediaAssets: (publicationId: string) =>
    request<{ items: import('@domain/studio').MediaAsset[] }>(
      `/api/publications/${publicationId}/media-assets`,
    ),
  createLibraryFixtureAsset: (publicationId: string, altText?: string) =>
    request<{ item: import('@domain/studio').MediaAsset }>(
      `/api/publications/${publicationId}/media-assets/fixture`,
      { method: 'POST', body: JSON.stringify({ altText }) },
    ),
  selectProductionAsset: (
    variantId: string,
    body: import('@domain/studio').SelectExistingAssetInput,
  ) =>
    request<{ item: import('@domain/studio').StudioVariantProductionView }>(
      `/api/platform-variants/${variantId}/production/select-asset`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  requestProductionRender: (
    variantId: string,
    body: import('@domain/studio').CreateFixtureMediaInput,
  ) =>
    request<{
      renderJob: import('@domain/studio').RenderJob;
      view: import('@domain/studio').StudioVariantProductionView;
    }>(`/api/platform-variants/${variantId}/production/render`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requestProductionRerender: (
    variantId: string,
    mode?: 'new_image' | 'carousel' | 'short_video',
  ) =>
    request<{
      renderJob: import('@domain/studio').RenderJob;
      view: import('@domain/studio').StudioVariantProductionView;
    }>(`/api/platform-variants/${variantId}/production/rerender`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  runStudioRenderJob: (jobId: string) =>
    request<{ item: import('@domain/studio').StudioVariantProductionView }>(
      `/api/studio/jobs/${jobId}/run`,
      { method: 'POST', body: '{}' },
    ),
  markProductionDeliverableReady: (
    variantId: string,
    deliverableId: string,
    altText?: string | null,
  ) =>
    request<{ item: import('@domain/studio').StudioVariantProductionView }>(
      `/api/platform-variants/${variantId}/production/deliverables/${deliverableId}/ready`,
      { method: 'POST', body: JSON.stringify({ altText }) },
    ),
  getProductionEligibility: (variantId: string) =>
    request<{
      production: import('@domain/studio').ProductionEligibility;
      downstream: import('@domain/studio').DownstreamEligibility;
    }>(`/api/platform-variants/${variantId}/production-eligibility`),
  getScheduleEligibility: (variantId: string) =>
    request<{ item: import('@domain/scheduler').ScheduleEligibility }>(
      `/api/platform-variants/${variantId}/schedule-eligibility`,
    ),
  bindVariantDestination: (variantId: string, publishingDestinationId: string) =>
    request<{ item: import('@domain/scheduler').ScheduleEligibility }>(
      `/api/platform-variants/${variantId}/publishing-destination`,
      {
        method: 'POST',
        body: JSON.stringify({ publishingDestinationId }),
      },
    ),
  listReadyToSchedule: (publicationId: string) =>
    request<{ items: import('@domain/scheduler').ReadyToScheduleItem[] }>(
      `/api/scheduler/ready?publicationId=${encodeURIComponent(publicationId)}`,
    ),
  listSchedulingRecommendations: (variantId: string) =>
    request<{ items: import('@domain/scheduler').SchedulingRecommendation[] }>(
      `/api/platform-variants/${variantId}/scheduling-recommendations`,
    ),
  createSchedulingRecommendation: (
    variantId: string,
    body?: import('@domain/scheduler').CreateRecommendationInput,
  ) =>
    request<{ item: import('@domain/scheduler').SchedulingRecommendation }>(
      `/api/platform-variants/${variantId}/scheduling-recommendations`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  listScheduledItemsForVariant: (variantId: string) =>
    request<{ items: import('@domain/scheduler').ScheduledItem[] }>(
      `/api/platform-variants/${variantId}/scheduled-items`,
    ),
  createScheduledItem: (
    variantId: string,
    body: import('@domain/scheduler').CreateScheduledItemInput,
  ) =>
    request<{
      item: import('@domain/scheduler').ScheduledItem;
      eligibility: import('@domain/scheduler').ScheduleEligibility;
    }>(`/api/platform-variants/${variantId}/scheduled-items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getScheduledItem: (id: string) =>
    request<{ item: import('@domain/scheduler').ScheduledItem }>(`/api/scheduled-items/${id}`),
  rescheduleScheduledItem: (
    id: string,
    body: import('@domain/scheduler').RescheduleScheduledItemInput,
  ) =>
    request<{
      item: import('@domain/scheduler').ScheduledItem;
      eligibility: import('@domain/scheduler').ScheduleEligibility;
    }>(`/api/scheduled-items/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cancelScheduledItem: (
    id: string,
    body?: import('@domain/scheduler').CancelScheduledItemInput,
  ) =>
    request<{ item: import('@domain/scheduler').ScheduledItem }>(
      `/api/scheduled-items/${id}/cancel`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  getScheduleHistory: (id: string) =>
    request<{ items: import('@domain/scheduler').ScheduleEvent[] }>(
      `/api/scheduled-items/${id}/history`,
    ),
  getExecutionReadiness: (id: string) =>
    request<{ item: import('@domain/scheduler').ExecutionReadiness }>(
      `/api/scheduled-items/${id}/execution-readiness`,
    ),
  refreshExecutionReadiness: (id: string) =>
    request<{
      item: import('@domain/scheduler').ScheduledItem;
      readiness: import('@domain/scheduler').ExecutionReadiness;
    }>(`/api/scheduled-items/${id}/refresh-readiness`, { method: 'POST', body: '{}' }),
  getSchedulerTimeline: (params: {
    publicationId: string;
    from?: string;
    to?: string;
    timezone?: string;
  }) => {
    const q = new URLSearchParams({ publicationId: params.publicationId });
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.timezone) q.set('timezone', params.timezone);
    return request<{ item: import('@domain/scheduler').TimelineDeskView }>(
      `/api/scheduler/timeline?${q.toString()}`,
    );
  },
  getSchedulerAgenda: (params: { publicationId: string; date: string; timezone?: string }) => {
    const q = new URLSearchParams({
      publicationId: params.publicationId,
      date: params.date,
    });
    if (params.timezone) q.set('timezone', params.timezone);
    return request<{ item: import('@domain/scheduler').AgendaDayView }>(
      `/api/scheduler/agenda?${q.toString()}`,
    );
  },
  getCampaignScheduleSummary: (campaignId: string) =>
    request<{ item: import('@domain/scheduler').CampaignScheduleSummary }>(
      `/api/campaigns/${campaignId}/schedule-summary`,
    ),
  enqueueScheduledItem: (
    scheduledItemId: string,
    body?: import('@domain/queue').EnqueueScheduledItemInput,
  ) =>
    request<{
      item: import('@domain/queue').QueueItem;
      job: import('@domain/queue').PublicationJob;
      asyncJob: import('@domain/jobs/types').AsyncJob;
      reused: boolean;
    }>(`/api/scheduled-items/${scheduledItemId}/enqueue?mode=fixture`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  getQueueDesk: (publicationId: string) =>
    request<{ item: import('@domain/queue').QueueDeskView }>(
      `/api/queue?publicationId=${encodeURIComponent(publicationId)}&mode=fixture`,
    ),
  listQueueReady: (publicationId: string) =>
    request<{
      items: Array<{
        scheduledItemId: string;
        platformVariantId: string;
        platformVariantVersionId: string;
        platform: string;
        format: string;
        scheduledAtUtc: string;
        timezone: string;
        localWallTime: string;
        publishingDestinationId: string | null;
        bodyPreview: string;
        alreadyQueued: boolean;
      }>;
    }>(`/api/queue/ready?publicationId=${encodeURIComponent(publicationId)}&mode=fixture`),
  getQueueItem: (id: string) =>
    request<{ item: import('@domain/queue').QueueItem }>(`/api/queue/items/${id}`),
  cancelQueueItem: (id: string, body?: import('@domain/queue').CancelQueueItemInput) =>
    request<{ item: import('@domain/queue').QueueItem }>(`/api/queue/items/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  listQueueEvents: (id: string) =>
    request<{ items: import('@domain/queue').OperationalEvent[] }>(
      `/api/queue/items/${id}/events`,
    ),
  listQueueRecords: (id: string) =>
    request<{ items: import('@domain/queue').PublicationRecord[] }>(
      `/api/queue/items/${id}/records`,
    ),
  runQueueJob: (jobId: string) =>
    request<{
      item: import('@domain/queue').QueueItem;
      job: import('@domain/queue').PublicationJob;
      record: import('@domain/queue').PublicationRecord | null;
      asyncJob: import('@domain/jobs/types').AsyncJob;
    }>(`/api/queue/jobs/${jobId}/run?mode=fixture`, { method: 'POST' }),
  retryQueueJob: (jobId: string) =>
    request<{
      item: import('@domain/queue').QueueItem;
      job: import('@domain/queue').PublicationJob;
      asyncJob: import('@domain/jobs/types').AsyncJob;
    }>(`/api/queue/jobs/${jobId}/retry?mode=fixture`, { method: 'POST' }),
  getTrackerDesk: (params: {
    publicationId: string;
    status?: string | null;
    platform?: string | null;
    destinationId?: string | null;
    campaignId?: string | null;
    provider?: string | null;
    q?: string | null;
    range?: string | null;
    fromUtc?: string | null;
    toUtc?: string | null;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set('publicationId', params.publicationId);
    if (params.status) qs.set('status', params.status);
    if (params.platform) qs.set('platform', params.platform);
    if (params.destinationId) qs.set('destinationId', params.destinationId);
    if (params.campaignId) qs.set('campaignId', params.campaignId);
    if (params.provider) qs.set('provider', params.provider);
    if (params.q) qs.set('q', params.q);
    if (params.range) qs.set('range', params.range);
    if (params.fromUtc) qs.set('fromUtc', params.fromUtc);
    if (params.toUtc) qs.set('toUtc', params.toUtc);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    return request<{ item: import('@domain/tracker').TrackerDeskView }>(
      `/api/tracker?${qs.toString()}`,
    );
  },
  getTrackerDetail: (id: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ item: import('@domain/tracker').TrackerEntryDetail }>(
      `/api/tracker/${id}${qs}`,
    );
  },
  listTrackerEvents: (id: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ items: import('@domain/queue').OperationalEvent[] }>(
      `/api/tracker/${id}/events${qs}`,
    );
  },
  listTrackerAttempts: (id: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ items: import('@domain/queue').ProviderExecutionRecord[] }>(
      `/api/tracker/${id}/attempts${qs}`,
    );
  },
  getCampaignTracker: (campaignId: string) =>
    request<{ item: import('@domain/tracker').CampaignTrackerSummary }>(
      `/api/campaigns/${campaignId}/tracker`,
    ),
  getPublicationLifecycle: (
    publicationId: string,
    opts?: { campaignId?: string; sourceId?: string },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.campaignId) qs.set('campaignId', opts.campaignId);
    if (opts?.sourceId) qs.set('sourceId', opts.sourceId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ item: import('@domain/tracker').PublicationLifecycleFacts }>(
      `/api/publications/${publicationId}/lifecycle${suffix}`,
    );
  },
  getBeaconDesk: (params: {
    publicationId: string;
    campaignId?: string | null;
    destinationId?: string | null;
    platform?: string | null;
    format?: string | null;
    perspectiveId?: string | null;
    tone?: string | null;
    q?: string | null;
    range?: string | null;
    fromUtc?: string | null;
    toUtc?: string | null;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set('publicationId', params.publicationId);
    if (params.campaignId) qs.set('campaignId', params.campaignId);
    if (params.destinationId) qs.set('destinationId', params.destinationId);
    if (params.platform) qs.set('platform', params.platform);
    if (params.format) qs.set('format', params.format);
    if (params.perspectiveId) qs.set('perspectiveId', params.perspectiveId);
    if (params.tone) qs.set('tone', params.tone);
    if (params.q) qs.set('q', params.q);
    if (params.range) qs.set('range', params.range);
    if (params.fromUtc) qs.set('fromUtc', params.fromUtc);
    if (params.toUtc) qs.set('toUtc', params.toUtc);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    return request<{ item: import('@domain/performance').BeaconDeskView }>(
      `/api/beacon?${qs.toString()}`,
    );
  },
  getBeaconPublication: (publicationRecordId: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ item: import('@domain/performance').BeaconPublicationDetail }>(
      `/api/beacon/publications/${publicationRecordId}${qs}`,
    );
  },
  getBeaconCampaign: (campaignId: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ item: import('@domain/performance').BeaconCampaignView }>(
      `/api/beacon/campaigns/${campaignId}${qs}`,
    );
  },
  getBeaconDestination: (destinationId: string, publicationId?: string) => {
    const qs = publicationId
      ? `?publicationId=${encodeURIComponent(publicationId)}`
      : '';
    return request<{ item: import('@domain/performance').BeaconDestinationView }>(
      `/api/beacon/destinations/${destinationId}${qs}`,
    );
  },
  refreshPerformance: (
    publicationRecordId: string,
    opts?: {
      publicationId?: string;
      fixtureMode?: string;
      nowUtc?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.publicationId) qs.set('publicationId', opts.publicationId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      ok: boolean;
      duplicate?: boolean;
      item?: import('@domain/performance').PerformanceSnapshotBundle;
      error?: { code: string; message: string };
    }>(`/api/performance/${publicationRecordId}/refresh${suffix}`, {
      method: 'POST',
      body: JSON.stringify({
        fixtureMode: opts?.fixtureMode ?? 'normal',
        nowUtc: opts?.nowUtc,
      }),
    });
  },
  issueMediaAccessToken: (assetId: string, publicationId: string, purpose = 'preview') =>
    request<{ item: { token: string; expiresAt: string; url: string } }>(
      `/api/media/assets/${assetId}/access-token`,
      { method: 'POST', body: JSON.stringify({ publicationId, purpose }) },
    ),
  listJobs: () => request<{ items: import('@domain/jobs/types').AsyncJob[] }>('/api/jobs'),
  enqueueJob: (body: unknown) =>
    request<{ item: import('@domain/jobs/types').AsyncJob }>('/api/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  retryJob: (jobId: string) =>
    request<{ item: import('@domain/jobs/types').AsyncJob }>(`/api/jobs/${jobId}/retry`, {
      method: 'POST',
    }),

  /** SPX-013 Source / Campaign lifecycle */
  getSourceCompletion: (sourceId: string, publicationId?: string) => {
    const qs = publicationId ? `?publicationId=${encodeURIComponent(publicationId)}` : '';
    return request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/completion${qs}`,
    );
  },
  completeSource: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/complete`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  setFeaturedMedia: (sourceId: string, imageUrl: string) =>
    request<{
      item: {
        status: 'ready' | 'pending' | 'unavailable' | 'failed_retryable' | 'failed_terminal';
        mediaAssetId: string | null;
        error: string | null;
      };
    }>(`/api/sources/${sourceId}/featured-media`, {
      method: 'POST',
      body: JSON.stringify({ imageUrl }),
    }),
  archiveSource: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/archive`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  restoreSource: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/restore`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  reopenSource: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/reopen`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listSourceLifecycleEvents: (sourceId: string, publicationId?: string) => {
    const qs = publicationId ? `?publicationId=${encodeURIComponent(publicationId)}` : '';
    return request<{ items: import('@domain/lifecycle').SourceLifecycleEvent[] }>(
      `/api/sources/${sourceId}/lifecycle-events${qs}`,
    );
  },
  /** Stage 19K — Recycle Bin (soft-delete). */
  softDeleteSource: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/soft-delete`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  restoreSourceFromBin: (sourceId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').SourceCompletionView }>(
      `/api/sources/${sourceId}/restore-from-bin`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  permanentlyDeleteSource: (sourceId: string, body: { publicationId?: string } = {}) =>
    request<{ item: { deleted: true; sourceId: string } }>(
      `/api/sources/${sourceId}/permanent-delete`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  getCampaignCompletion: (
    campaignId: string,
    opts?: { publicationId?: string; acknowledgeFailures?: boolean },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.publicationId) qs.set('publicationId', opts.publicationId);
    if (opts?.acknowledgeFailures) qs.set('acknowledgeFailures', '1');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ item: import('@domain/lifecycle').CampaignCompletionView }>(
      `/api/campaigns/${campaignId}/completion${suffix}`,
    );
  },
  completeCampaign: (
    campaignId: string,
    body: {
      publicationId?: string;
      note?: string;
      acknowledgeFailures?: boolean;
    } = {},
  ) =>
    request<{ item: import('@domain/lifecycle').CampaignCompletionView }>(
      `/api/campaigns/${campaignId}/complete`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  reopenCampaign: (campaignId: string, body: { publicationId?: string; note?: string } = {}) =>
    request<{ item: import('@domain/lifecycle').CampaignCompletionView }>(
      `/api/campaigns/${campaignId}/reopen`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listCampaignLifecycleEvents: (campaignId: string, publicationId?: string) => {
    const qs = publicationId ? `?publicationId=${encodeURIComponent(publicationId)}` : '';
    return request<{ items: import('@domain/lifecycle').CampaignLifecycleEvent[] }>(
      `/api/campaigns/${campaignId}/lifecycle-events${qs}`,
    );
  },

  /** SPX-014 Auth */
  bootstrap: () =>
    request<{
      mode: 'legacy' | 'required';
      user: import('@/app/state/AuthContext').AuthUserView | null;
      profile: import('@/app/state/AuthContext').AuthUserView['profile'] | null;
      organisations: import('@/app/state/AuthContext').AuthOrganisation[];
      publications: import('@domain/publication/types').Publication[];
    }>('/api/bootstrap'),
  authConfig: () =>
    request<{
      mode: 'legacy' | 'required';
      policyVersion: string;
      appOrigin?: string;
      publicWebsiteOrigin?: string;
      canonicalAppOrigin?: string;
    }>('/api/auth/config'),
  authRegister: (body: {
    fullName: string;
    email: string;
    password: string;
    organisationName?: string;
  }) =>
    request<{
      user: import('@/app/state/AuthContext').AuthUserView;
      organisations: import('@/app/state/AuthContext').AuthOrganisation[];
    }>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  authLogin: (body: { email: string; password: string }) =>
    request<{
      user: import('@/app/state/AuthContext').AuthUserView;
      organisations: import('@/app/state/AuthContext').AuthOrganisation[];
    }>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  authLogout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  authMe: () =>
    request<{
      user: {
        id: string;
        email: string;
        fullName: string;
        status: string;
        emailVerifiedAt: string | null;
        createdAt: string;
      };
      profile: {
        displayName: string;
        jobTitle: string | null;
        timezone: string | null;
        avatarMediaAssetId: string | null;
      };
      organisations: Array<{ id: string; name: string; slug: string }>;
      sessionId: string;
    }>('/api/auth/me'),
  authForgotPassword: (email: string) =>
    request<{ accepted: boolean }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  authResetPassword: (body: { token: string; newPassword: string }) =>
    request<{ ok: boolean }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  authVerifyEmail: (token: string) =>
    request<{ user: { id: string; emailVerifiedAt: string | null } }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  authUpdateProfile: (body: unknown) =>
    request<{
      user: { id: string; fullName: string; email: string };
      profile: {
        displayName: string;
        jobTitle: string | null;
        timezone: string | null;
        avatarMediaAssetId: string | null;
      };
    }>('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(body) }),
  authUploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{
      profile: {
        displayName: string;
        jobTitle: string | null;
        timezone: string | null;
        avatarMediaAssetId: string | null;
      };
    }>('/api/auth/avatar', { method: 'POST', body: form });
  },
  authDeleteAvatar: () =>
    request<{
      profile: {
        displayName: string;
        jobTitle: string | null;
        timezone: string | null;
        avatarMediaAssetId: string | null;
      };
    }>('/api/auth/avatar', { method: 'DELETE' }),
  authChangePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<{ ok: boolean }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  authUpdateOrganisation: (id: string, body: { name?: string }) =>
    request<{ item: { id: string; name: string; slug: string } }>(
      `/api/auth/organisation/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  authOrganisationTeam: (organisationId: string) =>
    request<{
      items: Array<{
        userId: string;
        email: string;
        fullName: string;
        displayName: string;
        role: string;
        status: string;
        createdAt: string;
      }>;
    }>(`/api/auth/organisation/${organisationId}/team`),
  authListSessions: () =>
    request<{
      items: Array<{
        id: string;
        createdAt: string;
        lastSeenAt: string;
        expiresAt: string;
        userAgent: string | null;
        current: boolean;
      }>;
    }>('/api/auth/sessions'),
  authRevokeOtherSessions: () =>
    request<{ ok: boolean }>('/api/auth/sessions/revoke-others', { method: 'POST' }),

  /** SPX-019I Integrations Control Centre */
  getIntegrationsDesk: (publicationId: string) =>
    request<{ item: import('@domain/integrations').IntegrationsDeskView }>(
      `/api/integrations?publicationId=${encodeURIComponent(publicationId)}`,
    ),
  testContentSource: (body: import('@domain/integrations').TestContentSourceInput & { publicationId?: string }) =>
    request<{ item: import('@domain/integrations').TestContentSourceResult }>(
      '/api/integrations/content-sources/test',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  connectContentSource: (body: import('@domain/integrations').ConnectContentSourceInput) =>
    request<{ item: import('@domain/integrations').ContentSourceSyncResult }>(
      '/api/integrations/content-sources',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  syncContentSource: (
    connectionId: string,
    trigger?: import('@domain/integrations').ContentSourceSyncTrigger,
  ) =>
    request<{ item: import('@domain/integrations').ContentSourceSyncResult }>(
      `/api/integrations/content-sources/${connectionId}/sync`,
      { method: 'POST', body: JSON.stringify({ trigger }) },
    ),
  listContentSourceSyncRuns: (connectionId: string) =>
    request<{ items: import('@domain/integrations').ContentSourceSyncRun[] }>(
      `/api/integrations/content-sources/${connectionId}/sync-runs`,
    ),
  updateContentSourceConnection: (
    connectionId: string,
    body: import('@domain/integrations').UpdateContentSourceConnectionInput,
  ) =>
    request<{ item: import('@domain/integrations').ContentSourceConnectionView }>(
      `/api/integrations/content-sources/${connectionId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  disconnectContentSource: (connectionId: string) =>
    request<{ item: import('@domain/integrations').ContentSourceConnectionView }>(
      `/api/integrations/content-sources/${connectionId}/disconnect`,
      { method: 'POST', body: '{}' },
    ),
  testPubler: (publicationId: string) =>
    request<{ item: import('@domain/integrations').PublerServiceStatusView }>(
      '/api/integrations/publer/test',
      { method: 'POST', body: JSON.stringify({ publicationId }) },
    ),
  saveDirectPublerCredentials: (body: import('@domain/integrations').SaveDirectPublerCredentialsInput) =>
    request<{ item: import('@domain/integrations').PublerServiceStatusView }>(
      '/api/integrations/publer/direct',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listAiProviders: (publicationId: string) =>
    request<{ items: import('@domain/integrations').AiProviderCredentialView[] }>(
      `/api/integrations/ai-providers?publicationId=${encodeURIComponent(publicationId)}`,
    ),
  testAiProviderKey: (body: import('@domain/integrations').TestAiProviderInput) =>
    request<{ item: import('@domain/integrations').TestAiProviderResult }>(
      '/api/integrations/ai-providers/test',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  addAiProviderCredential: (body: import('@domain/integrations').AddAiProviderCredentialInput) =>
    request<{ item: import('@domain/integrations').AiProviderCredentialView }>(
      '/api/integrations/ai-providers',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateAiProviderCredential: (
    id: string,
    publicationId: string,
    body: import('@domain/integrations').UpdateAiProviderCredentialInput,
  ) =>
    request<{ item: import('@domain/integrations').AiProviderCredentialView }>(
      `/api/integrations/ai-providers/${id}`,
      { method: 'PATCH', body: JSON.stringify({ ...body, publicationId }) },
    ),
  testExistingAiProviderCredential: (id: string, publicationId: string) =>
    request<{ item: import('@domain/integrations').TestAiProviderResult }>(
      `/api/integrations/ai-providers/${id}/test`,
      { method: 'POST', body: JSON.stringify({ publicationId }) },
    ),
  removeAiProviderCredential: (id: string, publicationId: string) =>
    request<{ ok: boolean }>(
      `/api/integrations/ai-providers/${id}?publicationId=${encodeURIComponent(publicationId)}`,
      { method: 'DELETE' },
    ),

  /** SPX-019L Add Content intake — URL / Paste Content / SEO Package. */
  importContentFromUrl: (publicationId: string, url: string) =>
    request<{ item: import('@domain/content-intake').ContentIntakeSourceResult }>(
      '/api/content-intake/url',
      { method: 'POST', body: JSON.stringify({ publicationId, url }) },
    ),
  pasteContent: (publicationId: string, content: string, sourceUrl?: string | null) =>
    request<{ item: import('@domain/content-intake').ContentIntakeSourceResult }>(
      '/api/content-intake/paste',
      { method: 'POST', body: JSON.stringify({ publicationId, content, sourceUrl }) },
    ),
  addSeoPackage: (body: import('@domain/content-intake').AddSeoPackageInput) =>
    request<{ item: import('@domain/content-intake').PendingContentPackage }>(
      '/api/content-intake/seo-package',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listPendingContentPackages: (publicationId: string) =>
    request<{ items: import('@domain/content-intake').PendingContentPackage[] }>(
      `/api/content-intake/pending?publicationId=${encodeURIComponent(publicationId)}`,
    ),
  checkPendingContentPackage: (id: string) =>
    request<{ item: import('@domain/content-intake').PendingContentPackage }>(
      `/api/content-intake/pending/${id}/check`,
      { method: 'POST', body: '{}' },
    ),
  attachUrlToPendingContentPackage: (id: string, url: string) =>
    request<{ item: import('@domain/content-intake').PendingContentPackage }>(
      `/api/content-intake/pending/${id}/url`,
      { method: 'POST', body: JSON.stringify({ url }) },
    ),
  linkPendingContentPackage: (id: string, sourceId: string) =>
    request<{ item: import('@domain/content-intake').PendingContentPackage }>(
      `/api/content-intake/pending/${id}/link`,
      { method: 'POST', body: JSON.stringify({ sourceId }) },
    ),
  dismissPendingContentPackage: (id: string) =>
    request<{ item: import('@domain/content-intake').PendingContentPackage }>(
      `/api/content-intake/pending/${id}/dismiss`,
      { method: 'POST', body: '{}' },
    ),
};
